// Copy for the per-page tours (usePageTour). Each step points at a
// data-tour attribute on the page; a step whose element is not on screen is
// dropped when the tour starts, so a page without a Super Dog this week, or a
// visitor with no Create league button, simply gets one step fewer.
//
// body may be a function of { demo }, for the few steps that read differently
// to a signed-out visitor in the public demo league.

const sel = (name) => `[data-tour="${name}"]`;

export const TOURS = {
  // Top to bottom down the page, then back up to the league to finish: the
  // last step is the one that sends a visitor somewhere, so it is the one
  // left on screen when the tour closes.
  home: [
    {
      el: sel("home-daily"),
      title: "Daily Games",
      body: "Two quick games, new every day. They're separate from the fantasy game and don't count toward your league.",
    },
    {
      el: sel("home-weekly"),
      title: "Pick Three, the weekly game",
      body: "Pick the three teams you think score the most Lineup points this week, and make one your captain to double its points. Each team locks at its own kickoff. It's free and separate from your league.",
    },
    // The games are for accounts only. In the demo this points at the bar's
    // Sign up button, which carries the home-join name.
    {
      el: sel("home-join"),
      demoOnly: true,
      title: "Sign up to play",
      body: "If you want to play these games, you need to have an account. You do not have to be in a league, but you have to have an account.",
    },
    {
      el: sel("home-games"),
      title: "This week's games",
      body: "Every game this week, with its betting spread. The spread matters here: beating it is how your teams score extra points.",
    },
    {
      el: sel("home-news"),
      title: "College football news",
      body: "The latest headlines from around the sport, updated through the day. Tap one to read it.",
    },
    {
      el: sel("home-top25"),
      title: "The top 25, by fantasy points",
      body: "The teams scoring the most points in this game, for the latest week or the season so far, and how many leagues own each one. A good place to find who is worth a bid.",
    },
    // Create and Join, for a member. A demo visitor has already been pointed
    // at Sign up by the games step above, and the guided tour ends there too.
    {
      el: sel("home-join"),
      memberOnly: true,
      title: "Want another league?",
      body: "Create one and invite friends, or join one with a code.",
    },
    // The league card's three parts, in the order they sit on the card, once
    // a league is scoring. The same three steps are the "?" beside Your
    // Leagues (Home.js). Before scoring starts the card has none of them and
    // these steps drop out.
    {
      el: sel("home-league-news"),
      title: "Breaking news",
      body: "What is happening in your league this week: big captain scores, 5x Plays, managers moving up or down the table, the week's top score, and waiver and free agent moves.",
    },
    {
      el: sel("home-league-lineup"),
      title: "Your lineup",
      body: "Your five starters and what each has scored this week, with your captain marked by the crown. Tap a team to see its game: the opponent, the score or kickoff, and the spread.",
    },
    {
      el: sel("home-league-table"),
      title: "The table at a glance",
      body: "The top six, or the top three and the managers either side of you, with season totals. The top six make the playoffs, and BYE marks the two who skip the first round. A gold ring means that manager has a 5x Play live. Tap a manager to see their lineup, or View all for the full table.",
    },
    {
      el: sel("home-league"),
      title: ({ demo }) => (demo ? "Now see how it plays" : "This is your league"),
      body: ({ demo, guided }) => (guided
        ? "You're managing Demo Squad, one of eight managers in this league. Next, how a week works: your lineup."
        : demo
          ? "You're managing Demo Squad, one of eight managers. Tap My Lineup to learn how it works: your five teams, your captain, and how they score. Each part of the league has its own short guide."
          : "Your rank and season points are on the card. Tap My Lineup to set this week's teams, or Standings to see the table."),
    },
  ],

  league: [
    {
      el: sel("league-table"),
      title: "The standings",
      body: "Managers are ranked by season points. Finish above the playoff line to make the playoffs.",
    },
    // The next four are also what the two "?" on the standings walk through
    // (the small one by the table's title takes the first two, the large one
    // over the stat columns the last two), and they stand in for the legends
    // that used to sit above the table. A manager holding no chips has no
    // chip element, so that step drops out for them.
    {
      el: sel("league-chips"),
      title: "What each manager still holds",
      body: "5X means their 5x Play is still unused. It fills in for the week it is in play. The snowflake counts the freezes they have left.",
    },
    {
      el: sel("league-dots"),
      title: "Five dots, five starters",
      body: "One dot for each starter this week. Blue is final, green is live, grey has not kicked off.",
    },
    {
      el: sel("league-columns"),
      title: "Total decides the table",
      body: "Total is season points, and it is what the table is ranked by. Wk is this week's points.",
    },
    // The popover renders its body as HTML, which is what the line breaks
    // rely on: the definitions read as a list, not as one long sentence.
    //
    // helpOnly: shown by the large "?" over the stat columns, which is where
    // someone looking for what a heading means goes, and left out of the page
    // tour, where a full glossary was more than a first look needs.
    {
      el: sel("league-columns-more"),
      helpOnly: true,
      title: "Swipe across for more",
      body: [
        "<b>Best</b> and <b>Worst</b> their highest and lowest finished week",
        "<b>Dec%</b> decision accuracy",
        "<b>Capt%</b> captain call accuracy",
        "<b>Capt Pts</b> points their captains have earned, 5x Play weeks aside",
        "<b>Bench</b> points left on the bench",
        "<b>FA</b> free agency and waiver moves",
      ].join("<br>") + "<br><br>All of these except FA count finished weeks only, so they update when the week closes. Tap any heading to sort the table by it.",
    },
    {
      el: `${sel("league-standings")} [data-tour-row]`,
      title: "Every manager's week",
      body: "Tap a manager to see which teams they started and what each one scored.",
    },
    {
      el: sel("league-weeks"),
      title: "Any week, any time",
      body: "Step back through earlier weeks to see how the table moved.",
    },
    {
      el: sel("league-pills"),
      title: "Everything else",
      body: "Recent moves, the members, the rules and the draft board are all one tap away.",
    },
    {
      el: sel("league-hof"),
      title: "Hall of Fame and Hall of Shame",
      body: "The league's record book: best and worst weeks, captain calls, 5x Plays and freezes. It updates once a week, when the week closes. Share any record with the share icon on its card.",
    },
  ],

  lineup: [
    {
      el: sel("lineup-starters"),
      title: "Five teams score each week",
      body: "Each starter earns 5 for a win and loses 3 for a defeat. Beating the spread adds up to 5 more, and missing it costs up to 5.",
    },
    {
      el: sel("lineup-captain"),
      title: "Your captain counts double",
      body: "Wins and losses both count twice, so pick a team you trust.",
    },
    {
      el: sel("lineup-tripplay"),
      title: "Once a season: the 5x Play",
      body: "Turn your captain into a 5x Play for one week between weeks 2 and 10. Five times the points, win or lose.",
    },
    {
      el: sel("lineup-bench"),
      title: "The bench doesn't score",
      body: "Swap a bench team in when it has the better matchup. Each team locks 15 minutes before its own kickoff.",
    },
    {
      el: sel("lineup-freezes"),
      title: "Three freezes a season",
      body: "From the second half of a game, you can freeze a team's score as it stands. It can't be undone, so time it well.",
    },
  ],

  scoreboard: [
    {
      el: sel("scoreboard-pills"),
      title: "Every lineup in the league",
      body: "Tap a name, or swipe, to see each manager's teams for the week.",
    },
    {
      el: sel("scoreboard-card"),
      title: "Scores update live",
      body: "Games are tracked live, so you can follow every point as it happens.",
    },
    {
      el: sel("scoreboard-superdog"),
      title: "The Super Dog",
      body: "An underdog by 14.5 points or more takes no penalty for losing. It can still lose points for a heavy defeat.",
    },
  ],

  teams: [
    {
      el: sel("teams-trending"),
      title: "What other managers are adding",
      body: "The teams picked up most across every league this week.",
    },
    {
      el: sel("teams-search"),
      title: "Find any team",
      body: "Search by name or filter by conference.",
    },
    {
      el: sel("teams-notice"),
      title: "How free agency works",
      body: "From week 4, bidding opens at 10am Sunday and closes at 4am Tuesday, Eastern. Rank your claims: they are tried in that order, and you only pay for the ones that win.",
    },
    {
      el: sel("teams-list"),
      title: "Scout before you add",
      body: "Tap any team for its schedule, recent form and stats. Teams nobody owns can be added once bidding has settled, first come, first served.",
    },
    // The guided demo tour's last word, on the Sign up button in the bar.
    {
      el: sel("demo-signup"),
      guidedOnly: true,
      title: "That's the tour",
      body: "Your own league works the same way, with your friends in it. Sign up now, or click around this one to explore more.",
    },
  ],
};
